import { ConfirmationService, MessageService } from 'primeng/api'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { Subject, takeUntil } from 'rxjs'
import { Router } from '@angular/router'

import { PillDef, SESSION_KIND_LABEL, SESSION_STATUS_PILL } from '../../data/constants/committee.constants'
import { CommitteeSessionListItem, SessionStatus } from '../../data/interfaces/committee-session.interface'
import { ApiCommitteeService } from '@data/services/modules/committee/api-committee.service'
import { CommitteeShellService } from '../../data/services/view/committee-shell.service'
import { AuthService } from '@data/services/modules/auth/auth.service'
import { fullName } from '../../data/utils/committee-tally.util'

/** Las tres lentes: qué sesiones se están mirando. */
export type SessionLens = 'open' | 'ready' | 'closed'

/**
 * La lista de sesiones del comité: cuáles esperan voto, a quién le falta y cuáles ya se pueden
 * cerrar.
 *
 * «Listas para cerrar» se filtra AQUÍ y no con otra consulta: `readyToClose` ya viene por fila del
 * servidor (D-11) y la lista de abiertas cabe en una página —nunca son más de un puñado—. Las
 * cerradas sí son otra consulta: ahí está el historial completo.
 */
@Component({
  selector: 'app-sessions-list',
  templateUrl: './sessions-list.component.html',
  styleUrls: ['./sessions-list.component.scss'],
  providers: [ConfirmationService],
})
export class SessionsListComponent implements OnInit, OnDestroy {
  lens: SessionLens = 'open'
  rows: CommitteeSessionListItem[] = []
  loading = true

  // ── Unificar sesiones abiertas ──
  /** `committee.update` es lo que exige `POST /sessions/:id/merge`: sin él no hay columna de selección. */
  canMerge = false
  selected = new Set<string>()
  merging = false
  error = ''

  readonly skeleton = [1, 2, 3, 4, 5]
  readonly kindLabel: Record<string, string> = SESSION_KIND_LABEL
  readonly statusPill: Partial<Record<string, PillDef>> = SESSION_STATUS_PILL

  private readonly destroy$ = new Subject<void>()

  constructor(
    private readonly api: ApiCommitteeService,
    private readonly shell: CommitteeShellService,
    private readonly router: Router,
    private readonly auth: AuthService,
    private readonly confirmation: ConfirmationService,
    private readonly messageService: MessageService,
  ) {
    this.canMerge = this.auth.hasModulePermission('committee', 'update')
  }

  ngOnInit(): void {
    //* Se creó una sesión, se votó o se cerró desde otra vista: la lista se vuelve a pedir.
    this.shell.refresh$.pipe(takeUntil(this.destroy$)).subscribe(() => this.load())
    this.load()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  /** Qué estado le pide al servidor la lente activa. */
  get status(): SessionStatus {
    return this.lens === 'closed' ? 'closed' : 'open'
  }

  get visibleRows(): CommitteeSessionListItem[] {
    return this.lens === 'ready' ? this.rows.filter((r) => r.readyToClose) : this.rows
  }

  /** Cuántas abiertas ya se pueden cerrar. Es el número de la lente, no de la página. */
  get readyCount(): number | null {
    return this.status === 'open' ? this.rows.filter((r) => r.readyToClose).length : null
  }

  setLens(lens: SessionLens): void {
    if (lens === this.lens) return
    //* Abiertas y Listas comparten consulta (`open`); Cerradas es otra. Solo se vuelve a pedir al
    //* cruzar esa frontera.
    const reload = lens === 'closed' || this.lens === 'closed'
    this.lens = lens
    this.selected.clear()
    if (reload) this.load()
  }

  open(row: CommitteeSessionListItem): void {
    this.router.navigate(['/comites/sesion', row._id])
  }

  // ── Selección para unificar ──

  /** Solo con permiso y mirando abiertas: las cerradas y las de registro no se unifican. */
  get showSelection(): boolean {
    return this.canMerge && this.status === 'open'
  }

  get selectable(): CommitteeSessionListItem[] {
    return this.visibleRows.filter((r) => r.status === 'open' && r.kind === 'voting')
  }

  get selectedRows(): CommitteeSessionListItem[] {
    return this.rows.filter((r) => this.selected.has(r._id))
  }

  get allSelected(): boolean {
    const rows = this.selectable
    return rows.length > 0 && rows.every((r) => this.selected.has(r._id))
  }

  isSelected(row: CommitteeSessionListItem): boolean {
    return this.selected.has(row._id)
  }

  toggle(row: CommitteeSessionListItem): void {
    if (row.status !== 'open' || row.kind !== 'voting') return
    if (this.selected.has(row._id)) this.selected.delete(row._id)
    else this.selected.add(row._id)
  }

  toggleAll(): void {
    if (this.allSelected) this.selectable.forEach((r) => this.selected.delete(r._id))
    else this.selectable.forEach((r) => this.selected.add(r._id))
  }

  /** La que se conserva: la más antigua, que es la fecha real en que el comité sesionó. */
  get mergeTarget(): CommitteeSessionListItem | null {
    const rows = this.selectedRows
    if (rows.length < 2) return null
    return rows.reduce((oldest, r) => (new Date(r.sessionDate) < new Date(oldest.sessionDate) ? r : oldest))
  }

  confirmMerge(): void {
    const target = this.mergeTarget
    if (!target || this.merging) return
    const others = this.selectedRows.filter((r) => r._id !== target._id)
    const fecha = (r: CommitteeSessionListItem) => new Date(r.sessionDate).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })
    const lines = others.map((r) => `<li>Sesión del ${fecha(r)} · ${r.itemsCount} ${r.itemsCount === 1 ? 'arrendamiento' : 'arrendamientos'}</li>`).join('')
    this.confirmation.confirm({
      key: 'unificar',
      header: 'Unificar sesiones',
      message: `<p>Se moverán a la <b>sesión del ${fecha(target)}</b> (la más antigua, que se conserva):</p><ul class="sd-confirm-list">${lines}</ul><p>Se unen votantes y actas; las otras sesiones se borran. No se deshace.</p>`,
      icon: 'pi pi-clone',
      acceptLabel: 'Unificar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-sm',
      rejectButtonStyleClass: 'p-button-sm p-button-text',
      accept: () => this.merge(target, others),
    })
  }

  private merge(target: CommitteeSessionListItem, others: CommitteeSessionListItem[]): void {
    this.merging = true
    this.api
      .mergeSessions(
        target._id,
        others.map((r) => r._id),
      )
      .subscribe((r) => {
        this.merging = false
        if (r.error) {
          this.messageService.add({ severity: 'error', summary: 'No se pudo', detail: r.msg || 'Ocurrió un error.' })
          return
        }
        this.selected.clear()
        this.messageService.add({ severity: 'success', summary: 'Sesiones unificadas', detail: `${others.length + 1} sesiones quedaron en una.` })
        this.shell.refresh()
        this.router.navigate(['/comites/sesion', target._id])
      })
  }

  /** Cuántos convocados ya votaron TODO, de cuántos. */
  votersDone(row: CommitteeSessionListItem): number {
    return (row.voters ?? []).filter((v) => v.pendingCount === 0).length
  }

  /** A quién le falta algo, por nombre. Vacío cuando ya votaron todos. */
  missingVoters(row: CommitteeSessionListItem): string {
    return (row.voters ?? [])
      .filter((v) => v.pendingCount > 0)
      .map((v) => fullName(v.user))
      .join(', ')
  }

  private load(): void {
    this.loading = true
    this.selected.clear()
    this.error = ''
    this.api.getSessions(this.status).subscribe((r) => {
      this.loading = false
      if (r.error) {
        this.rows = []
        this.error = r.msg || 'No se pudieron cargar las sesiones.'
        return
      }
      this.rows = r.data ?? []
    })
  }
}
